import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ModalDocumentActionButtons from '../common/ModalDocumentActionButtons';
import type { AssignmentPointsItem } from '../../types/assignmentPointsNote';
import {
  buildIncompleteByAssignmentRows,
  buildIncompleteByMemberRows,
  formatIncompleteReportTimestamp,
  incompleteKindBadgeStyle,
  type IncompleteReportMember,
} from '../../utils/assignmentIncompleteReport';
import {
  printModalBlockContentA4,
  saveModalBlockContentAsPdf,
} from '../../utils/modalDocumentExport';

export type IncompleteReportMode = 'byMember' | 'byAssignment';

type Props = {
  open: boolean;
  mode: IncompleteReportMode;
  items: AssignmentPointsItem[];
  members: IncompleteReportMember[];
  onClose?: () => void;
  /** modal: 오버레이 모달(기본). inline: 페이지/패널 본문에 직접 표시 */
  variant?: 'modal' | 'inline';
};

const TITLE: Record<IncompleteReportMode, string> = {
  byMember: '멤버별 미완료 과제',
  byAssignment: '과제별 미완료 멤버',
};

/** 모달 화면(블럭·뱃지·테두리)을 그대로 캡처하기 위한 라이트 인쇄 CSS
 *  (선택자는 scopeCaptureCssToExportShell / onclone 셸 클래스로 스코프됨)
 */
const CAPTURE_CSS = `
.cp-apn-incomplete-export,
.cp-apn-incomplete-export-meta,
.cp-apn-incomplete-export-body {
  background: #ffffff !important;
  color: #111111 !important;
  box-sizing: border-box;
  overflow: visible !important;
  max-height: none !important;
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
}
.cp-apn-incomplete-export-title {
  display: block !important;
  margin: 0 0 6px !important;
  font-size: 18px !important;
  font-weight: 800 !important;
  color: #111111 !important;
  text-align: center !important;
}
.cp-apn-incomplete-export-ts {
  margin: 0 0 14px !important;
  text-align: center !important;
  font-size: 12px !important;
  color: #666666 !important;
}
.cp-apn-incomplete-list {
  display: flex !important;
  flex-direction: column !important;
  gap: 10px !important;
}
.cp-apn-incomplete-row {
  display: flex !important;
  align-items: flex-start !important;
  gap: 14px !important;
  padding: 12px 14px !important;
  border: 1px solid #d8d8d8 !important;
  border-radius: 12px !important;
  background: #ffffff !important;
  box-sizing: border-box !important;
}
.cp-apn-incomplete-row--asgn {
  align-items: stretch !important;
}
.cp-apn-incomplete-member {
  flex: 0 0 88px !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 2px !important;
}
.cp-apn-incomplete-member-no {
  font-size: 12px !important;
  font-weight: 700 !important;
  color: #888888 !important;
}
.cp-apn-incomplete-member-name {
  font-size: 14px !important;
  font-weight: 700 !important;
  color: #111111 !important;
}
.cp-apn-incomplete-cards {
  flex: 1 1 auto !important;
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 8px !important;
  min-width: 0 !important;
}
.cp-apn-incomplete-card,
.cp-apn-incomplete-asgn-info {
  border: 1px solid #d8d8d8 !important;
  border-radius: 10px !important;
  padding: 8px 10px !important;
  background: #ffffff !important;
  box-sizing: border-box !important;
}
.cp-apn-incomplete-card {
  min-width: 148px !important;
  max-width: 240px !important;
}
.cp-apn-incomplete-asgn-info {
  flex: 0 0 200px !important;
  max-width: 240px !important;
}
.cp-apn-incomplete-card-meta {
  display: flex !important;
  align-items: center !important;
  gap: 6px !important;
  flex-wrap: wrap !important;
}
.cp-apn-incomplete-badge {
  display: inline-flex !important;
  align-items: center !important;
  padding: 1px 7px !important;
  border-radius: 4px !important;
  color: #ffffff !important;
  font-size: 10px !important;
  font-weight: 800 !important;
  line-height: 1.4 !important;
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
}
.cp-apn-incomplete-date {
  font-size: 12px !important;
  color: #888888 !important;
}
.cp-apn-incomplete-card-title {
  margin-top: 5px !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  color: #111111 !important;
  line-height: 1.35 !important;
}
.cp-apn-incomplete-tags {
  flex: 1 1 auto !important;
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 6px !important;
  align-content: flex-start !important;
  min-width: 0 !important;
}
.cp-apn-incomplete-tag {
  display: inline-flex !important;
  align-items: center !important;
  padding: 5px 9px !important;
  border-radius: 8px !important;
  background: #eceff3 !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  color: #222222 !important;
}
.cp-apn-incomplete-empty {
  margin: 28px 0 !important;
  text-align: center !important;
  color: #888888 !important;
}
`;

const AssignmentIncompleteReportModal: React.FC<Props> = ({
  open,
  mode,
  items,
  members,
  onClose,
  variant = 'modal',
}) => {
  const exportRootRef = useRef<HTMLDivElement>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const title = TITLE[mode];
  const timestamp = useMemo(() => formatIncompleteReportTimestamp(), [open, mode]);

  const memberRows = useMemo(
    () => (mode === 'byMember' ? buildIncompleteByMemberRows(items, members) : []),
    [mode, items, members]
  );
  const assignmentRows = useMemo(
    () =>
      mode === 'byAssignment' ? buildIncompleteByAssignmentRows(items, members) : [],
    [mode, items, members]
  );

  const runCapture = useCallback(
    async (kind: 'pdf' | 'print') => {
      const root = exportRootRef.current;
      if (!root) return;
      setExportBusy(true);
      try {
        if (kind === 'pdf') {
          await saveModalBlockContentAsPdf({
            root,
            fileName: `${title}.pdf`,
            bodySelector: '.cp-apn-incomplete-export-body',
            captureCss: CAPTURE_CSS,
            captureMinWidthPx: 900,
            disableSegmentedTableFallback: true,
          });
        } else {
          await printModalBlockContentA4({
            root,
            title,
            bodySelector: '.cp-apn-incomplete-export-body',
            captureCss: CAPTURE_CSS,
            captureMinWidthPx: 900,
            disableSegmentedTableFallback: true,
          });
        }
      } catch (e) {
        console.error(`미완료 리포트 ${kind === 'pdf' ? 'PDF' : '프린트'} 실패:`, e);
        alert(kind === 'pdf' ? 'PDF 저장에 실패했습니다.' : '프린트에 실패했습니다.');
      } finally {
        setExportBusy(false);
      }
    },
    [title]
  );

  if (!open) return null;

  const isInline = variant === 'inline';

  const body = (
    <div className="cp-apn-incomplete-export-body">
      {mode === 'byMember' ? (
        memberRows.length === 0 ? (
          <p className="cp-apn-incomplete-empty">미완료 과제가 있는 멤버가 없습니다.</p>
        ) : (
          <div className="cp-apn-incomplete-list">
            {memberRows.map((row) => (
              <div key={row.memberId} className="cp-apn-incomplete-row">
                <div className="cp-apn-incomplete-member">
                  <span className="cp-apn-incomplete-member-no">{row.orderIndex}번</span>
                  <span className="cp-apn-incomplete-member-name">{row.memberName}</span>
                </div>
                <div className="cp-apn-incomplete-cards">
                  {row.cards.map((card) => (
                    <div
                      key={`${row.memberId}-${card.itemId}`}
                      className="cp-apn-incomplete-card"
                    >
                      <div className="cp-apn-incomplete-card-meta">
                        <span
                          className="cp-apn-incomplete-badge"
                          style={incompleteKindBadgeStyle(card.accentColor)}
                        >
                          {card.kindLabel}
                        </span>
                        <span className="cp-apn-incomplete-date">{card.dateLabel}</span>
                      </div>
                      <div className="cp-apn-incomplete-card-title">{card.title}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      ) : assignmentRows.length === 0 ? (
        <p className="cp-apn-incomplete-empty">미완료 멤버가 있는 과제가 없습니다.</p>
      ) : (
        <div className="cp-apn-incomplete-list">
          {assignmentRows.map((row) => (
            <div
              key={row.itemId}
              className="cp-apn-incomplete-row cp-apn-incomplete-row--asgn"
            >
              <div className="cp-apn-incomplete-asgn-info">
                <div className="cp-apn-incomplete-card-meta">
                  <span
                    className="cp-apn-incomplete-badge"
                    style={incompleteKindBadgeStyle(row.accentColor)}
                  >
                    {row.kindLabel}
                  </span>
                  <span className="cp-apn-incomplete-date">{row.dateLabel}</span>
                </div>
                <div className="cp-apn-incomplete-card-title">{row.title}</div>
              </div>
              <div className="cp-apn-incomplete-tags">
                {row.members.map((m) => (
                  <span
                    key={`${row.itemId}-${m.memberId}`}
                    className="cp-apn-incomplete-tag"
                  >
                    {m.orderIndex}번 {m.memberName}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (isInline) {
    return (
      <div
        className="cp-apn-incomplete-inline"
        role="region"
        aria-label={title}
      >
        <div ref={exportRootRef} className="cp-apn-incomplete-export">
          {body}
        </div>
      </div>
    );
  }

  return createPortal(
    <div
      className="cp-apn-incomplete-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="cp-apn-incomplete-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-apn-incomplete-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cp-apn-incomplete-header">
          <h2 id="cp-apn-incomplete-title" className="cp-apn-incomplete-title">
            {title}
          </h2>
          <div className="cp-apn-incomplete-header-actions">
            <ModalDocumentActionButtons
              onSavePdf={() => void runCapture('pdf')}
              onPrint={() => void runCapture('print')}
              onClose={onClose}
              busy={exportBusy}
            />
          </div>
        </header>

        <div ref={exportRootRef} className="cp-apn-incomplete-export">
          <div className="cp-apn-incomplete-export-meta" data-pdf-element="header">
            <h3 className="cp-apn-incomplete-export-title">{title}</h3>
            <p className="cp-apn-incomplete-export-ts">{timestamp}</p>
          </div>
          {body}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AssignmentIncompleteReportModal;
